/**
 * Tournament Standings Service
 * Fetches match and game data and computes standings
 */

import { supabase } from '../utils/supabase';
import { computeGroupStandings } from '../utils/tournamentStandings';
import { entryToLegacyTeamShape } from '../utils/tournamentFormat';
import { getWalkoverPointsFor, normalizeScoringConfig } from '../utils/gameScoreRules';

const entrySelectFragment = `
  *,
  tournament_entry_players (
    position,
    player:player_id (*)
  )
`;

/**
 * Get standings for a single group
 */
export const getGroupStandings = async (tournamentId, groupId) => {
  // Get tournament for ranking rules
  const { data: tournament, error: tournamentError } = await supabase
    .from('tournaments')
    .select('ranking_order, tiebreaker_order, points_target, score_validation')
    .eq('id', tournamentId)
    .single();

  if (tournamentError) throw tournamentError;

  // Get entries in group
  const { data: groupEntries, error: groupEntriesError } = await supabase
    .from('tournament_group_entries')
    .select(`
      entry_id,
      tournament_entries (
        ${entrySelectFragment}
      )
    `)
    .eq('group_id', groupId);

  if (groupEntriesError) throw groupEntriesError;

  const teams = (groupEntries || [])
    .map((ge) => entryToLegacyTeamShape(ge.tournament_entries))
    .filter(Boolean);

  // Get matches for this group
  const { data: matches, error: matchesError } = await supabase
    .from('tournament_matches')
    .select('*')
    .eq('group_id', groupId);

  if (matchesError) throw matchesError;

  // Get all session_games for these matches
  const matchIds = matches.map((m) => m.id);
  const { data: games, error: gamesError } = await supabase
    .from('session_games')
    .select('*')
    .in('tournament_match_id', matchIds);

  if (gamesError) throw gamesError;

  const hasWalkoverOrForfeit = (matches || []).some(
    (m) => m.status === 'walkover' || m.status === 'forfeit'
  );

  const standings = computeGroupStandings(
    teams,
    matches,
    games || [],
    tournament.ranking_order,
    tournament.tiebreaker_order,
    { walkoverPointsFor: getWalkoverPointsFor(normalizeScoringConfig(tournament)) },
  );

  return { standings, hasWalkoverOrForfeit };
};

/**
 * Get standings for all groups in a tournament
 */
export const getAllGroupStandings = async (tournamentId) => {
  // Get all groups for tournament
  const { data: groups, error: groupsError } = await supabase
    .from('tournament_groups')
    .select('id, name, sort_order')
    .eq('tournament_id', tournamentId)
    .order('sort_order');

  if (groupsError) throw groupsError;

  // Get standings for each group
  const allStandings = [];

  for (const group of groups) {
    const { standings, hasWalkoverOrForfeit } = await getGroupStandings(tournamentId, group.id);
    allStandings.push({
      groupId: group.id,
      groupName: group.name,
      standings,
      hasWalkoverOrForfeit
    });
  }

  return allStandings;
};

/**
 * Get match details with games and team info
 */
export const getMatchDetails = async (matchId) => {
  const { data: match, error: matchError } = await supabase
    .from('tournament_matches')
    .select(`
      *,
      team1:entry1_id (
        ${entrySelectFragment}
      ),
      team2:entry2_id (
        ${entrySelectFragment}
      ),
      winner_team:winner_entry_id (
        ${entrySelectFragment}
      )
    `)
    .eq('id', matchId)
    .single();

  if (matchError) throw matchError;

  // Get games for this match
  const { data: games, error: gamesError } = await supabase
    .from('session_games')
    .select('*')
    .eq('tournament_match_id', matchId)
    .order('game_order');

  if (gamesError) throw gamesError;

  return {
    ...match,
    games: games || []
  };
};

/**
 * Get all matches for a tournament with details
 */
export const getTournamentMatches = async (tournamentId, round = null) => {
  let query = supabase
    .from('tournament_matches')
    .select(`
      *,
      team1:entry1_id (
        ${entrySelectFragment}
      ),
      team2:entry2_id (
        ${entrySelectFragment}
      ),
      winner_team:winner_entry_id (
        ${entrySelectFragment}
      ),
      tournament_groups (
        name
      )
    `)
    .eq('tournament_id', tournamentId)
    .order('match_order');

  if (round) {
    query = query.eq('round', round);
  }

  const { data: matches, error } = await query;

  if (error) throw error;

  // Get games for all matches
  const matchIds = matches.map((m) => m.id);
  const { data: allGames } = await supabase
    .from('session_games')
    .select('*')
    .in('tournament_match_id', matchIds)
    .order('game_order');

  // Attach games to matches
  const matchesWithGames = matches.map((match) => ({
    ...match,
    games: allGames?.filter((g) => g.tournament_match_id === match.id) || []
  }));

  return matchesWithGames;
};

/**
 * Get tournament summary statistics
 */
export const getTournamentSummary = async (tournamentId) => {
  const { data: tournament, error: tournamentError } = await supabase
    .from('tournaments')
    .select(`
      *,
      tournament_entries (id),
      tournament_groups (id),
      tournament_matches (
        id,
        status,
        round
      )
    `)
    .eq('id', tournamentId)
    .single();

  if (tournamentError) throw tournamentError;

  const teamCount = tournament.tournament_entries?.length || 0;
  const groupCount = tournament.tournament_groups?.length || 0;
  const matches = tournament.tournament_matches || [];

  const matchStats = {
    total: matches.length,
    scheduled: matches.filter((m) => m.status === 'scheduled').length,
    in_progress: matches.filter((m) => m.status === 'in_progress').length,
    completed: matches.filter((m) => m.status === 'completed').length,
    walkover: matches.filter((m) => m.status === 'walkover').length,
    forfeit: matches.filter((m) => m.status === 'forfeit').length
  };

  const roundStats = {
    group: matches.filter((m) => m.round === 'group').length,
    quarter_final: matches.filter((m) => m.round === 'quarter_final').length,
    semi_final: matches.filter((m) => m.round === 'semi_final').length,
    final: matches.filter((m) => m.round === 'final').length
  };

  return {
    ...tournament,
    teamCount,
    groupCount,
    matchStats,
    roundStats
  };
};

/**
 * Get match queue (scheduled matches by court)
 */
export const getMatchQueue = async (tournamentId) => {
  const { data: matches, error } = await supabase
    .from('tournament_matches')
    .select(`
      *,
      team1:entry1_id (
        ${entrySelectFragment}
      ),
      team2:entry2_id (
        ${entrySelectFragment}
      )
    `)
    .eq('tournament_id', tournamentId)
    .in('status', ['scheduled', 'in_progress'])
    .order('court_number')
    .order('match_order');

  if (error) throw error;

  // Group by court
  const byCourt = {};

  for (const match of matches || []) {
    const court = match.court_number || 'unassigned';
    if (!byCourt[court]) {
      byCourt[court] = [];
    }
    byCourt[court].push(match);
  }

  return byCourt;
};

/**
 * Get tournament winner (if completed)
 */
export const getTournamentWinner = async (tournamentId) => {
  // Get final match
  const { data: finalMatch, error } = await supabase
    .from('tournament_matches')
    .select(`
      winner_entry_id,
      tournament_entries:winner_entry_id (
        ${entrySelectFragment}
      )
    `)
    .eq('tournament_id', tournamentId)
    .eq('round', 'final')
    .eq('status', 'completed')
    .single();

  if (error || !finalMatch) return null;

  return finalMatch.tournament_entries;
};
